import React, {
    createContext,
    useContext,
    useEffect,
    useRef,
} from 'react';
import type { ReactNode } from 'react';

import * as amplitude from '@amplitude/analytics-browser';
import { Experiment, Source } from '@amplitude/experiment-js-client';
import { useSelector, useDispatch } from 'react-redux';

import {
    experimentEligibilityReceived,
    experimentsSelectors,
    fetchClientVariants,
    fetchExperimentEligibility,
    variantsSelectors,
} from 'wanderu-analytics';
import appSettings from 'wanderu-config';
import Logger from 'wanderu-logger';
import { SnowplowService } from 'wanderu-service-analytics';

import type { ExperimentEligibility } from './experimentsSlice';
import type { EnrichmentPlugin, BrowserConfig, Event } from '@amplitude/analytics-types';
import type { ExperimentClient } from '@amplitude/experiment-js-client';

const logger = Logger.getLogger('AmplitudeProvider');

interface AmplitudeContextType {
    amplitude: typeof amplitude;
    experiment?: ExperimentClient;
}

interface AmplitudeProviderProps {
    children: ReactNode;
}

const AmplitudeContext = createContext<AmplitudeContextType | undefined>(
    undefined,
);

class SnowplowIdsPlugin implements EnrichmentPlugin {
    name = 'snowplow-ids-plugin';

    type = 'enrichment' as const;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars, class-methods-use-this
    async setup(config: BrowserConfig) {
        return undefined;
    }

    // eslint-disable-next-line class-methods-use-this
    async execute(event: Event) {
        const newEvent = event;

        const {
            domainUserId,
            domainSessionId,
            domainSessionIdx,
        } = SnowplowService.getDomainUserInfo() as {
            domainUserId: string;
            domainSessionId: string;
            domainSessionIdx: number;
        };

        const networkUserId = SnowplowService.getNetworkUserId() as string;

        if (!newEvent.event_properties) {
            newEvent.event_properties = {};
        }

        newEvent.event_properties.domain_session_id = domainSessionId;
        newEvent.event_properties.domain_session_idx = domainSessionIdx;
        newEvent.event_properties.domain_user_id = domainUserId;
        newEvent.event_properties.network_user_id = networkUserId;

        return newEvent;
    }
}

let experiment: ExperimentClient | undefined;

export function AmplitudeProvider({ children }: AmplitudeProviderProps) {
    const variants = useSelector(variantsSelectors.selectEntities);
    const experimentEligibility: ExperimentEligibility = useSelector(
        experimentsSelectors.selectExperimentEligibility,
    );
    const hasFetched = useSelector(variantsSelectors.selectHasFetched);
    const dispatch = useDispatch();
    const eligibilityRequestedRef = useRef(false);
    const identifySentRef = useRef(false);

    const apiKey = appSettings?.config?.amplitude?.apiKey;

    useEffect(() => {
        if (!apiKey) {
            logger.event('Amplitude', 'apiKey not found');

            return;
        }

        amplitude.init(apiKey, { // Browser Analytics https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#configure-the-sdk
            appVersion: appSettings?.env?.build?.version,
            autocapture: {
                attribution: {
                    resetSessionOnNewCampaign: true,
                },
                elementInteractions: true,
                fileDownloads: true,
                formInteractions: true,
                pageViews: {
                    trackOn: () => {
                    // disabling auto page views for pages that manually track them
                        return !window.location.pathname.includes('depart')
                            && !window.location.pathname.includes('return')
                            && !window.location.pathname.includes('checkout')
                            && !window.location.pathname.includes('tripsummary');
                    },
                },
                sessions: true,
            },
            serverUrl: appSettings?.config?.amplitude?.serverUrl,
        })
            .promise
            .then(
                (result) => {
                    const deviceId = amplitude.getDeviceId() as string;

                    logger.event('Amplitude', 'loaded', { deviceId, result });

                    amplitude.add(new SnowplowIdsPlugin()); // Add snowplow ids to every Amplitude event

                    SnowplowService.addGlobalContexts([{
                        data: {
                            ampClientId: deviceId,
                        },
                        schema: 'iglu:dev.amp.snowplow/amp_id/jsonschema/1-0-0',
                    }]);
                },
                (error) => {
                    const { deviceId } = experimentEligibility;

                    logger.event('Amplitude', 'failed', { deviceId, error });
                }
            );

        if (!experiment) {
        // in the client, we only want to create the ExperimentClient once
            experiment = Experiment.initializeWithAmplitudeAnalytics(apiKey, {
                automaticExposureTracking: false,
                initialVariants: { ...variants },
                source: Source.InitialVariants,
            });
        }
    }, []);

    useEffect(() => {
        if (
            !apiKey
            || experimentEligibility?.isEligibleForAssignment !== null
            || eligibilityRequestedRef.current
        ) {
            return;
        }

        eligibilityRequestedRef.current = true;

        fetchExperimentEligibility()
            .then((payload) => {
                dispatch(experimentEligibilityReceived(payload));
            })
            .catch((error) => {
                logger.error('Amplitude', 'eligibility_fetch_failed', { error });
            });
    }, [apiKey, dispatch, experimentEligibility]);

    useEffect(() => {
        if (
            !experimentEligibility
            || experimentEligibility.isEligibleForAssignment === null
            || identifySentRef.current
        ) {
            return;
        }

        identifySentRef.current = true;

        const {
            isEligibleForAssignment,
            userProperties,
        } = experimentEligibility;

        const { userAgent } = userProperties || {};

        // Note: Sensitive data (cfBotScore, ja4, ipAddress, asn) is now sent
        // server-side via sendServerSideAmplitudeIdentify() for security
        const identifyEvent = new amplitude.Identify();

        identifyEvent.set('is_eligible_for_assignment', isEligibleForAssignment);
        if (userAgent) identifyEvent.set('user_agent', userAgent);

        amplitude.identify(identifyEvent);
    }, [experimentEligibility]);

    useEffect(() => {
        // fetch experiment variants when the client side boots only if we haven't already on the server
        if (!experiment || hasFetched || experimentEligibility?.isEligibleForAssignment !== true) {
            return;
        }
        fetchClientVariants({
            deviceId: experimentEligibility.deviceId,
            dispatch,
            experiment,
        });
    }, [dispatch, experimentEligibility, hasFetched]);

    return (
        <AmplitudeContext.Provider value={{ amplitude, experiment }}>
            {children}
        </AmplitudeContext.Provider>
    );
}

export { experiment };

export function useAmplitude() {
    const context = useContext(AmplitudeContext);

    if (context === undefined) {
        throw new Error('useAmplitude must be used within an AmplitudeProvider');
    }

    return context;
}
